import { TeamSubscriptionFlags } from 'lib/generated_constants/subscriptions';
import { OrganizationSubscriptionFlags } from 'lib/generated_constants/subscriptions';

export type SubscriptionType =
  | ({
      id: string;
      features: string[] | undefined;
    } & Record<string, unknown>)
  | null
  | undefined;

export class SubscriptionService {
  // Utility
  static hasFlag(
    subscription: SubscriptionType,
    flag:
      | (typeof TeamSubscriptionFlags)[keyof typeof TeamSubscriptionFlags]
      | (typeof OrganizationSubscriptionFlags)[keyof typeof OrganizationSubscriptionFlags],
  ) {
    return !!subscription?.features?.includes(flag);
  }

  static hasAdvancedScreening(subscription: SubscriptionType) {
    return this.hasFlag(subscription, TeamSubscriptionFlags.ADVANCED_SCREENING);
  }

  static hasOccupationTargeting(subscription: SubscriptionType) {
    return this.hasFlag(
      subscription,
      TeamSubscriptionFlags.OCCUPATION_TARGETING,
    );
  }

  /** True when team or (optional) organization subscription includes invite-filter feature. */
  static hasPopulationInviteFilters(
    teamSubscription: SubscriptionType,
    organizationSubscription?: SubscriptionType,
  ) {
    return (
      this.hasFlag(
        teamSubscription,
        TeamSubscriptionFlags.POPULATION_INVITE_FILTERS,
      ) ||
      this.hasFlag(
        organizationSubscription,
        OrganizationSubscriptionFlags.POPULATION_INVITE_FILTERS,
      )
    );
  }

  static hasDocumentSigning(subscription: SubscriptionType) {
    return this.hasFlag(subscription, TeamSubscriptionFlags.DOCUMENT_SIGNING);
  }

  static hasRecruitSubscription(subscription: SubscriptionType) {
    return this.hasFlag(subscription, TeamSubscriptionFlags.RECRUIT_UNLIMITED);
  }

  static hasHubSubscription(subscription: SubscriptionType) {
    return this.hasFlag(subscription, TeamSubscriptionFlags.PRIVATE_POPULATION);
  }

  static hasBranding(subscription: SubscriptionType) {
    return this.hasFlag(subscription, TeamSubscriptionFlags.BRANDING);
  }

  static hasDomains(subscription: SubscriptionType) {
    return this.hasFlag(subscription, TeamSubscriptionFlags.DOMAINS);
  }

  static hasEmailTemplates(subscription: SubscriptionType) {
    return this.hasFlag(subscription, TeamSubscriptionFlags.EMAIL_TEMPLATES);
  }

  static hasPrivatePopulation(subscription: SubscriptionType) {
    return this.hasFlag(subscription, TeamSubscriptionFlags.PRIVATE_POPULATION);
  }

  static isLegacySubscription(subscription: SubscriptionType) {
    return !!(subscription && subscription.isLegacy);
  }

  static isLegacyHubSubscription(subscription: SubscriptionType) {
    return !!(subscription && subscription.isLegacyHub);
  }

  static isLegacyRecruitSubscription(subscription: SubscriptionType) {
    return !!(subscription && subscription.isLegacyRecruit);
  }

  // Organization-wide subscription flag methods
  static hasOrgBranding(subscription: SubscriptionType) {
    return this.hasFlag(subscription, OrganizationSubscriptionFlags.BRANDING);
  }

  static hasOrgDomains(subscription: SubscriptionType) {
    return this.hasFlag(subscription, OrganizationSubscriptionFlags.DOMAINS);
  }

  static hasOrgEmailTemplates(subscription: SubscriptionType) {
    return this.hasFlag(
      subscription,
      OrganizationSubscriptionFlags.EMAIL_TEMPLATES,
    );
  }

  static hasOrgPrivatePopulation(subscription: SubscriptionType) {
    return this.hasFlag(
      subscription,
      OrganizationSubscriptionFlags.PRIVATE_POPULATION,
    );
  }

  static hasOrgDocumentSigning(subscription: SubscriptionType) {
    return this.hasFlag(
      subscription,
      OrganizationSubscriptionFlags.DOCUMENT_SIGNING,
    );
  }

  static hasOrgSsoAvailable(subscription: SubscriptionType) {
    return this.hasFlag(
      subscription,
      OrganizationSubscriptionFlags.SSO_AVAILABLE,
    );
  }
}
